import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Mail, Phone, MapPin, DollarSign, FileText, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency, formatDate, getEstadoInfo } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SERVICIOS_PER_PAGE = 10;

const ClienteDetalle = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [cliente, setCliente] = useState(null);
    const [servicios, setServicios] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [clienteRes, serviciosRes] = await Promise.all([
                axios.get(`${API_URL}/api/clientes/${id}`),
                axios.get(`${API_URL}/api/servicios?cliente_id=${id}&page_size=100`)
            ]);
            setCliente(clienteRes.data);
            // Handle paginated response
            setServicios(serviciosRes.data.items || serviciosRes.data);
        } catch (error) {
            console.error('Error fetching cliente:', error);
            toast.error('Error al cargar el cliente');
            navigate('/clientes');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9A9A]"></div>
            </div>
        );
    }

    if (!cliente) return null;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/clientes')}>
                    <ArrowLeft size={20} />
                </Button>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-slate-900">{cliente.razon_social}</h1>
                    <p className="text-slate-500">{cliente.rut}</p>
                </div>
                <Badge className={cliente.activo ? 'badge-success' : 'badge-error'}>
                    {cliente.activo ? 'Activo' : 'Inactivo'}
                </Badge>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D5E8E8] rounded-lg flex items-center justify-center">
                            <FileText className="text-[#5B9A9A]" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{cliente.total_servicios}</p>
                            <p className="text-xs text-slate-500">Servicios</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="text-slate-600" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(cliente.monto_total)}</p>
                            <p className="text-xs text-slate-500">Total facturado</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="text-amber-600" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(cliente.monto_pendiente)}</p>
                            <p className="text-xs text-slate-500">Pendiente</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                            <AlertTriangle className="text-rose-600" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-rose-600">{formatCurrency(cliente.monto_vencido)}</p>
                            <p className="text-xs text-slate-500">Vencido</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info and Services */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Client Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Información del Cliente</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {cliente.direccion && (
                            <div className="flex items-start gap-3">
                                <MapPin className="text-slate-400 mt-0.5" size={18} />
                                <div>
                                    <p className="text-xs text-slate-500">Dirección</p>
                                    <p className="text-sm">{cliente.direccion}</p>
                                </div>
                            </div>
                        )}
                        {cliente.email_cobranza && (
                            <div className="flex items-start gap-3">
                                <Mail className="text-slate-400 mt-0.5" size={18} />
                                <div>
                                    <p className="text-xs text-slate-500">Email cobranza</p>
                                    <p className="text-sm">{cliente.email_cobranza}</p>
                                </div>
                            </div>
                        )}
                        {cliente.telefono && (
                            <div className="flex items-start gap-3">
                                <Phone className="text-slate-400 mt-0.5" size={18} />
                                <div>
                                    <p className="text-xs text-slate-500">Teléfono</p>
                                    <p className="text-sm">{cliente.telefono}</p>
                                </div>
                            </div>
                        )}
                        {cliente.contacto_financiero && (
                            <div className="flex items-start gap-3">
                                <Building2 className="text-slate-400 mt-0.5" size={18} />
                                <div>
                                    <p className="text-xs text-slate-500">Contacto financiero</p>
                                    <p className="text-sm">{cliente.contacto_financiero}</p>
                                </div>
                            </div>
                        )}
                        <div className="pt-4 border-t">
                            <p className="text-xs text-slate-500">Condición de pago</p>
                            <p className="text-sm font-medium">{cliente.condicion_pago_dias} días</p>
                        </div>
                        {cliente.observaciones && (
                            <div className="pt-4 border-t">
                                <p className="text-xs text-slate-500">Observaciones</p>
                                <p className="text-sm">{cliente.observaciones}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Services */}
                <Card className="lg:col-span-2">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-base">Servicios ({servicios.length})</CardTitle>
                        <Button size="sm" onClick={() => navigate('/servicios')}>
                            Nuevo servicio
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {servicios.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                <FileText size={48} className="mx-auto text-slate-300 mb-2" />
                                <p>No hay servicios registrados</p>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-3">
                                    {servicios
                                        .slice((currentPage - 1) * SERVICIOS_PER_PAGE, currentPage * SERVICIOS_PER_PAGE)
                                        .map((servicio) => {
                                            const estadoInfo = getEstadoInfo(servicio.estado);
                                            return (
                                                <div
                                                    key={servicio.id}
                                                    className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                                                    onClick={() => navigate(`/servicios/${servicio.id}`)}
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-slate-900 truncate">
                                                                {servicio.nombre_producto || servicio.descripcion}
                                                                {servicio.periodo_mes && servicio.periodo_anio && (
                                                                    <span className="text-slate-400 font-normal ml-2">
                                                                        ({servicio.periodo_mes}/{servicio.periodo_anio})
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-sm text-slate-500 mt-1">
                                                                {servicio.numero_factura || 'Sin facturar'} • 
                                                                Vence: {formatDate(servicio.fecha_vencimiento)}
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-semibold">{formatCurrency(servicio.monto_total)}</p>
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${estadoInfo.color}`}>
                                                                {estadoInfo.label}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                                
                                {/* Pagination */}
                                {servicios.length > SERVICIOS_PER_PAGE && (
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                        <p className="text-sm text-slate-500">
                                            Mostrando {((currentPage - 1) * SERVICIOS_PER_PAGE) + 1} - {Math.min(currentPage * SERVICIOS_PER_PAGE, servicios.length)} de {servicios.length}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                            >
                                                <ChevronLeft size={16} />
                                            </Button>
                                            <span className="text-sm px-2">
                                                {currentPage} / {Math.ceil(servicios.length / SERVICIOS_PER_PAGE)}
                                            </span>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentPage(p => Math.min(Math.ceil(servicios.length / SERVICIOS_PER_PAGE), p + 1))}
                                                disabled={currentPage >= Math.ceil(servicios.length / SERVICIOS_PER_PAGE)}
                                            >
                                                <ChevronRight size={16} />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ClienteDetalle;
